import { differenceInMilliseconds, isValid, parseISO } from "date-fns";
import { useMemo } from "react";
import { object, string, StringSchema } from "yup";

import { ValidationErrorType } from "@/lib/hook/useValidationSync";
import { parseProseMirrorSchema } from "@/lib/proseMirror";

import { ImmigramFormElementType } from "@/components/ImmigramForm/elements/common";

export default function useApplicationFormValidationSchema(formConfig: any) {
  return useMemo(() => {
    const fieldsWithRequiredGroupMap = formConfig.reduce(
      (acc: Record<string, any>, field: any) => {
        const { requiredGroup } = field.validation || {};

        if (!requiredGroup) {
          return acc;
        }

        return {
          ...acc,
          [requiredGroup]: [...(acc[requiredGroup] || []), field.name],
        };
      },
      {}
    );

    const shape = formConfig.reduce((acc: Record<string, any>, field: any) => {
      if (!field.validation) return acc;

      if (!field.name) {
        throw new Error("Field name is required for validation");
      }

      acc[field.name] = string();

      if (field.type === ImmigramFormElementType.Textarea) {
        acc[field.name] = acc[field.name].transform(parseProseMirrorSchema);
      }

      if (field.validation.minLength) {
        acc[field.name] = acc[field.name].min(field.validation.minLength);
      }

      if (field.validation.required) {
        acc[field.name] = acc[field.name].required();
      }

      if (field.validation.greaterThanRolling) {
        acc[field.name] = (acc[field.name] as StringSchema).test(
          ValidationErrorType.greaterThanRolling,
          (date) => {
            if (
              !date ||
              !isValid(parseISO(date)) ||
              field.type !== ImmigramFormElementType.Date
            ) {
              return true;
            }
            const millisecondsDiff =
              date && differenceInMilliseconds(new Date(), parseISO(date));

            if (
              millisecondsDiff &&
              millisecondsDiff > field.validation.greaterThanRolling
            ) {
              return false;
            }
            return true;
          }
        );
      }

      if (field.validation.requiredGroup) {
        acc[field.name] = (acc[field.name] as StringSchema).test(
          ValidationErrorType.Required,
          (value, { parent }) => {
            const validationGroupFields =
              fieldsWithRequiredGroupMap[field.validation.requiredGroup] || [];

            return !!validationGroupFields.some(
              (fieldName: string) => !!parent[fieldName]
            );
          }
        );
      }

      return acc;
    }, {});

    return object().shape(shape);
  }, [formConfig]);
}
